import React, {Fragment, Suspense, useEffect, useState} from 'react';
import {
    Backdrop,
    Box,
    CircularProgress,
    Grid,
    makeStyles,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import {Alert} from '@material-ui/lab';

import useStyles from '../styles/styles';
import {useLazyQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import {format} from 'date-fns';
import {navigate} from '@reach/router';

import LeadNavigationMenu from "../components/Leads/LeadNavigationMenu";
import WarningIcon from '@material-ui/icons/Warning';
import CurrencyField from "../components/Fields/CurrencyField";
import PhoneField from "../components/Fields/PhoneField";
import SensitiveField from "../components/Fields/SensitiveField";
import BankInformation from "../components/Leads/BankInformation";
import VehicleInformation from "../components/Leads/VehicleInformation";
import theme from '../styles/theme';
import ParseResponse from '../utils/ParseResponse';
import setDate from '../utils/setDate';
import convertToCurrency from '../utils/convertToCurrency';
import dashes from 'add-dashes-to-uuid';
import LoanRedirectDataTableShortForm from "../components/Leads/LoanRedirectDataTableShortForm";
import MarketingRedirectTableShortForm from "../components/Leads/MarketingRedirectTableShortForm";
import CampaignFilterResultsTableShortForm from "../components/Leads/CampaignFilterResultsTableShortForm";
import CloudwatchLogsTableShortForm from "../components/Leads/CloudwatchLogsTableShortForm";
import IdentityEventsTableShortForm from "../components/Leads/IdentityEventsTableShortForm";
import ApiRequestDecisionTable from "../components/Leads/ApiRequestDecisionTable";
import PerformanceSummaryTable from "../components/Leads/PerformanceSummaryTable";
import ApiResultInformationShortForm from "../components/Leads/ApiResultInformationShortForm";
import ApiRequestLogTableShortForm from "../components/Leads/ApiRequestLogTableShortForm";
import SignatureBlocksTableShortForm from "../components/Leads/SignatureBlocksTableShortForm";
import ApiResponseInformationShortForm from "../components/Leads/ApiResponseInformationShortForm";
import ExperimentRecordsShortForm from "../components/Leads/ExperimentRecordsShortForm";
import BidsTableShortForm from "../components/Leads/BidsTableShortForm";

const useBoxStyles = makeStyles({
    root: {
        '& .MuiPaper-elevation1': {
            height: '400px',
            overflow: 'scroll'
        }
    },
});

const ShortFormLead = ({leadId}) => {
    const id = leadId.includes('-') ? leadId : dashes(leadId);
    const classes = useStyles();
    const boxClasses = useBoxStyles();

    const [modalData, setModalData] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasSignatureBlock, setHasSignatureBlock] = useState(false);
    const [loadComponents, setLoadComponents] = useState(false);
    const openModal = info => {
        setModalData(info);
        setIsModalOpen(true);
    };
    const closeModal = () => {
        setIsModalOpen(false);
    };

    const [load, {loading, error, data}] = useLazyQuery(gql`
        query LeadPage($apiRequestId: UUID!) {
            apiRequest(apiRequestId: $apiRequestId) {
                apiRequest {
                    requestId
                    requestDateCreated
                    clientIpAddress
                    everFlowTransactionId
                    userAgent
                    pingTree
                    timeout
                    sourceKey
                    sourceLeadId
                    sourceSessionId
                    sourceSubCode1
                    sourceSubCode1Hash
                    sourceSubCode2
                    sourceSubCode2Hash
                    sourceSubCode3
                    isTestApp
                    minimumLoanAmount
                    maximumLoanAmount
                    minimumPrice
    
                    # Person data
                    firstName
                    middleName
                    lastName
                    primaryEmail
                    homePhone
                    primaryPhone
                    mobilePhone
                    workPhone
                    workExt
                    dateOfBirth
                    military
                    ssn
                    lastFourSsn
                    stateIdNumber
                    stateIdNumberHash
                    stateIdIssued
                    verifiedCustomer
    
                    # HomeAddress
                    streetAddress
                    streetAddress2
                    homeCity
                    state
                    homeZip
                    residenceType
                    residenceLengthMonths
    
                    # Employment Info
                    employerName
                    employerLengthMonths
                    employmentType
                    employmentSubType
    
                    # Income Info
                    payFrequency
                    monthlyIncome
                    nextPayDate
                    secondPayDate
                    payType
    
                    # Bank Info
                    bankName
                    bankRouting
                    bankAccount
                    bankAccountType
                    monthsAtBank
                    
                    # Vehicle Info
                    vehicleMake
                    vehicleModel
                    vehicleTrim
                    vehicleYear
                    vehicleMileage
                    
                    titleAsCollateral
                    titleClear
                    
                    referringSiteUrl
                }
                trafficSource {
                    id
                    name
                    medium
                    origin
                    channel
                    legacyZoneId
                    legacyPromoId
                    legacyOfferId
                    affiliate {
                        id
                        name
                        legacyPublisherId
                    }
                    payoutStrategyEnum
                }
                campaignMinPrice {
                    campaignId
                    createdAt
                    id
                    minPrice
                    requestId
                }
            }
        }
    `, {
        variables: {
            apiRequestId: id,
        },
        context: {
            clientName: "kgraphql"
        }
    });

    const [loadApiRequestDecisions, {
        loading: apiRequestDecisionsLoading,
        error: apiRequestDecisionsError,
        data: apiRequestDecisionsData
    }] = useLazyQuery(gql`
        query ApiRequestDecisions($apiRequestId: UUID!, $apiRequestDateCreated: String!) {
            apiRequestDecisions(apiRequestId: $apiRequestId, apiRequestDateCreated: $apiRequestDateCreated) {
                nodes {
                    entityId
                    timestamp
                    requestId
                    servicer
                    portfolio
                    campaignId
                    campaignType
                    status
                    pricePoint
                    billable
                    payable
                    request
                    response
                    result
                    requestTimestamp
                    responseTimestamp
                    statusCode
                    tierMin
                    type
                    elapsedTime
                    vendorMessage
                    vendorSessionId
                    strategy
                }
                performanceSummaryData {
                    id
                    timeToFirstCampaignHttpRequest
                    totalSynchronousPostingTime
                }
            }
        }
    `, {
        variables: {
            apiRequestId: id,
            apiRequestDateCreated: data?.apiRequest?.apiRequest?.requestDateCreated,
        },
        context: {
            clientName: "kgraphql"
        }
    });

    useEffect(() => {
        load()
    }, [])

    useEffect(() => {
        if (data) {
            loadApiRequestDecisions()
        }
    }, [data])

    useEffect(() => {
        setTimeout(() => {
            setLoadComponents(true);
        }, [3000])
    }, [data]);

    if (loading) {
        return (
            <Backdrop className={classes.backdrop} open={loading}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        )
    }

    if (error) {
        return (
            <Paper className={classes.paper}>
                <Alert severity="error">Well, this in embarrassing&hellip; something went wrong.</Alert>
                <p>
                    <a href="/#" onClick={(e) => {
                        e.preventDefault();
                        navigate('/info/leads');
                    }}>Click here to view all leads.</a>
                </p>
            </Paper>
        )
    }

    if (!data || !data.apiRequest.apiRequest) {
        return (
            <>
                <Typography component="h2">
                    LoanRequest data is invalid
                </Typography>
                <pre>${JSON.stringify(data, null, 2)}</pre>
            </>
        )
    }

    data.apiRequest.apiRequest = data.apiRequest.apiRequest || {}

    data.apiRequest.apiRequest.localRequestDateCreated = setDate(data.apiRequest.apiRequest.requestDateCreated)
    data.apiRequest.apiRequest.dateOfBirth = new Date(data.apiRequest.apiRequest.dateOfBirth)
    data.apiRequest.apiRequest.nextPayDate = new Date(data.apiRequest.apiRequest.nextPayDate)
    data.apiRequest.apiRequest.secondPayDate = new Date(data.apiRequest.apiRequest.secondPayDate)


    const signatureBlockBanner = () => {
        return hasSignatureBlock ? (
            <div style={{backgroundColor: theme.palette.warning.main}} className={classes.signatureNotification}
                 onClick={(e) => {
                     window.scrollTo(0, document.querySelector("#signature-block-table").offsetTop);
                 }}>
                <WarningIcon style={{fontSize: '300%', marginRight: theme.spacing(1)}}/>
                <Typography background={"secondary"} variant="h2" component="h2"
                            className={classes.monospace + " " + classes.blockSignature} id={"page-top"}
                > SIGNATURE BLOCKED </Typography>
            </div>

        ) : <></>
    }


    return (
        <>
            <Typography variant="h2" component="h2" className={classes.monospace} id={"page-top"}>
                {id}
            </Typography>
            {signatureBlockBanner()}
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Created At" value={data.apiRequest.apiRequest.localRequestDateCreated || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="IP Address" value={data.apiRequest.apiRequest.clientIpAddress || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="User Agent" value={data.apiRequest.apiRequest.userAgent || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Traffic Source" value={data.apiRequest.trafficSource.name || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Minimum Price"
                                   value={data.apiRequest.apiRequest.minimumPrice !== null ? convertToCurrency(data.apiRequest.apiRequest.minimumPrice) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Minimum Loan Amount"
                                   value={data.apiRequest.apiRequest.minimumLoanAmount !== null ? convertToCurrency(data.apiRequest.apiRequest.minimumLoanAmount) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Maximum Loan Amount"
                                   value={data.apiRequest.apiRequest.maximumLoanAmount !== null ? convertToCurrency(data.apiRequest.apiRequest.maximumLoanAmount) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                        <TextField label="Ping Tree" value={data.apiRequest.apiRequest.pingTree || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Channel" value={data.apiRequest.trafficSource.channel || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Origin" value={data.apiRequest.trafficSource.origin || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Medium" value={data.apiRequest.trafficSource.medium || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                        <TextField label="Affiliate" value={data.apiRequest.trafficSource.affiliate.name || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                        <TextField label="Payout Strategy"
                                   value={data.apiRequest.trafficSource.payoutStrategyEnum || ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <TextField label="Affiliate Legacy Publisher ID"
                                   value={data.apiRequest.trafficSource.affiliate.legacyPublisherId || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Zone ID" value={data.apiRequest.trafficSource.legacyZoneId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Promo ID" value={data.apiRequest.trafficSource.legacyPromoId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Offer ID" value={data.apiRequest.trafficSource.legacyOfferId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Timeout" value={data.apiRequest.apiRequest.timeout || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={1}>
                        <TextField label="Is Test App" value={data.apiRequest.apiRequest.isTestApp || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={5}>
                        <TextField label="Source Key" value={data.apiRequest.apiRequest.sourceKey || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Source Lead Id" value={data.apiRequest.apiRequest.sourceLeadId || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Source Session Id" value={data.apiRequest.apiRequest.sourceSessionId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Source Sub Code 1" value={data.apiRequest.apiRequest.sourceSubCode1 || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Source Sub Code 1 Hash"
                                   value={data.apiRequest.apiRequest.sourceSubCode1Hash || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Source Sub Code 2" value={data.apiRequest.apiRequest.sourceSubCode2 || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Source Sub Code 2 Hash"
                                   value={data.apiRequest.apiRequest.sourceSubCode2Hash || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField label="Source Sub Code 3" value={data.apiRequest.apiRequest.sourceSubCode3 || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Everflow Transaction Id"
                                   value={data.apiRequest.apiRequest.everflowTransactionId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Referring Site URL" value={data.apiRequest.apiRequest.referringSiteUrl || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                </Grid>
            </Paper>


            <ApiResultInformationShortForm apiRequestId={data.apiRequest.apiRequest.requestId}
                                           apiRequestDateCreated={data.apiRequest.apiRequest.requestDateCreated}
            />

            <Typography variant="h4" component="h4" align="left">Performance Summary</Typography>
            <PerformanceSummaryTable data={apiRequestDecisionsData?.apiRequestDecisions?.performanceSummaryData}
                                     loading={apiRequestDecisionsLoading}
                                     error={apiRequestDecisionsError}/>

            <Typography variant="h4" component="h4" align="left" id={"person"}>Person</Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="First Name" value={data.apiRequest.apiRequest.firstName || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="Middle Name" value={data.apiRequest.apiRequest.middleName || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="Last Name" value={data.apiRequest.apiRequest.lastName || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={2} lg={1}>
                        <TextField label="Military" value={data.apiRequest.apiRequest.military ? 'TRUE' : 'FALSE'}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={5}>
                        <TextField label="Email" value={data.apiRequest.apiRequest.primaryEmail || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Primary Phone"
                                   value={data.apiRequest.apiRequest.primaryPhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Home Phone"
                                   value={data.apiRequest.apiRequest.homePhone ? data.apiRequest.apiRequest.homePhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3") : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Mobile Phone"
                                   value={data.apiRequest.apiRequest.mobilePhone ? data.apiRequest.apiRequest.mobilePhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3") : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Street Address" value={data.apiRequest.apiRequest.homeStreetAddress || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={1}>
                        <TextField label="Line 2" value={data.apiRequest.apiRequest.homeStreetAddress2 || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="City" value={data.apiRequest.apiRequest.homeCity || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={3} lg={1}>
                        <TextField label="State" value={data.apiRequest.apiRequest.state || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={3} lg={1}>
                        <TextField label="ZIP Code" value={data.apiRequest.apiRequest.homeZip || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Residence Type" value={data.apiRequest.apiRequest.residenceType || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Residence Length"
                                   value={data.apiRequest.apiRequest.residenceLengthMonths ? `${data.apiRequest.apiRequest.residenceLengthMonths} months` || '' : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Date of Birth"
                                   value={format(data.apiRequest.apiRequest.dateOfBirth, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                    {data.apiRequest.apiRequest.ssn && !data.apiRequest.apiRequest.lastFourSsn ?
                        <Grid item xs={12} sm={6} lg={3}>
                            <SensitiveField label="Social Security Number"
                                            value={data.apiRequest.apiRequest.ssn?.replace(/(\d{3})(\d{2})(\d{4})/, "$1–$2–$3") ?? ''}/>
                        </Grid>
                        :
                        <Grid item xs={12} sm={6} lg={3}>
                            <SensitiveField label="Social Security Number Last Four"
                                            value={data.apiRequest.apiRequest.lastFourSsn ?? ''}/>
                        </Grid>
                    }

                    <Grid item xs={12} sm={8} lg={2}>
                        <SensitiveField label="State ID Number" value={data.apiRequest.apiRequest.stateIdNumber || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="State ID Issued" value={data.apiRequest.apiRequest.stateIdIssued || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <TextField label="State ID Hash" value={data.apiRequest.apiRequest.stateIdNumberHash || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <TextField label="Verified"
                                   value={data.apiRequest.apiRequest.verifiedCustomer ? 'TRUE' : 'FALSE'}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="h4" component="h4" align="left">Employment Information</Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5} lg={3}>
                        <TextField label="Type" value={data.apiRequest.apiRequest.employmentType || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={3}>
                        <TextField label="SubType" value={data.apiRequest.apiRequest.employmentSubType || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Employment Length"
                                   value={data.apiRequest.apiRequest.employerLengthMonths ? `${data.apiRequest.apiRequest.employerLengthMonths} months` || '' : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Employer Name" value={data.apiRequest.apiRequest.employerName || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={3}>
                        <PhoneField label="Phone" value={data.apiRequest.apiRequest.workPhone || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={3}>
                        <TextField label="Ext" value={data.apiRequest.apiRequest.workExt || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <CurrencyField label="Monthly Income" value={data.apiRequest.apiRequest.monthlyIncome || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <TextField label="Pay Type" value={data.apiRequest.apiRequest.payType || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Pay Frequency" value={data.apiRequest.apiRequest.payFrequency || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Next Pay Date"
                                   value={format(data.apiRequest.apiRequest.nextPayDate, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Second Pay Date"
                                   value={format(data.apiRequest.apiRequest.secondPayDate, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                </Grid>
            </Paper>

            <BankInformation
                bankName={data.apiRequest.apiRequest.bankName ?? ''}
                accountType={data.apiRequest.apiRequest.bankAccountType ?? ''}
                routingNumber={data.apiRequest.apiRequest.bankRouting ?? ''}
                accountNumber={data.apiRequest.apiRequest.bankAccount ?? ''}
                monthsAtBank={data.apiRequest.apiRequest.monthsAtBank ?? ''}
            />

            <VehicleInformation
                vehicleMake={data.apiRequest.apiRequest.vehicleMake}
                vehicleModel={data.apiRequest.apiRequest.vehicleModel}
                vehicleTrim={data.apiRequest.apiRequest.vehicleTrim}
                vehicleYear={data.apiRequest.apiRequest.vehicleYear}
                vehicleMileage={data.apiRequest.apiRequest.vehicleMileage}
                titleAsCollateral={data.apiRequest.apiRequest.titleAsCollateral}
                titleClear={data.apiRequest.apiRequest.titleClear}
            />

            <ApiResponseInformationShortForm apiRequestId={id}
                                             apiRequestDateCreated={data.apiRequest.apiRequest.requestDateCreated}/>

            <ExperimentRecordsShortForm apiRequestId={id}/>

            <BidsTableShortForm apiRequestId={id}/>

            <ApiRequestDecisionTable data={apiRequestDecisionsData?.apiRequestDecisions?.nodes}
                                     loading={apiRequestDecisionsLoading}
                                     error={apiRequestDecisionsError} openModal={openModal}/>

            <ApiRequestLogTableShortForm apiRequestId={data.apiRequest.apiRequest.requestId}/>

            {data.apiRequest.apiRequest ?
                <Suspense fallback={<span></span>}>
                    <CampaignFilterResultsTableShortForm apiRequestId={data.apiRequest.apiRequest.requestId}
                                                         apiRequestDateCreated={data.apiRequest.apiRequest.requestDateCreated}/>
                    <LoanRedirectDataTableShortForm apiRequestId={data.apiRequest.apiRequest.requestId}
                                                    apiRequestDateCreated={data.apiRequest.apiRequest.requestDateCreated}/>
                    <MarketingRedirectTableShortForm apiRequestId={data.apiRequest.apiRequest.requestId}
                                                     apiRequestDateCreated={data.apiRequest.apiRequest.requestDateCreated}/>
                </Suspense>
                : null}

            {data.apiRequest.apiRequest.campaignMinPrice ? (
                <>
                    <Typography variant="h4" component="h4" align="left" id={"campaign-min-data"}>Campaign Min
                        Data</Typography>
                    <Paper className={classes.paper}>
                        <Table className={classes.table} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell variant="head">Campaign Id</TableCell>
                                    <TableCell variant="head">Min Price</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.apiRequest.apiRequest.campaignMinPrice.map(minPrice => (
                                    <TableRow key={minPrice.id}>
                                        <TableCell variant="body">{minPrice.campaignId}</TableCell>
                                        <TableCell variant="body">{convertToCurrency(minPrice.minPrice)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            ) : null}

            <IdentityEventsTableShortForm apiRequestId={id}/>

            <SignatureBlocksTableShortForm
                apiRequestId={id}
                setHasSignatureBlock={setHasSignatureBlock}/>

            {id ? (
                <CloudwatchLogsTableShortForm id={id}/>
            ) : null}

            <LeadNavigationMenu
                hasCampaignDescisionLogs={apiRequestDecisionsData?.apiRequestDecisions?.nodes.length}
                hasRawRequestLogs={data.apiRequest.loanRequestLog}
                hasLoanRedirect={true}
                hasCampaignMinPrice={data.apiRequest.campaignMinPrice}
            />

            <Modal
                open={isModalOpen}
                onClose={closeModal}
            >
                <Paper className={classes.modal}>
                    <h2>{modalData.title}</h2>

                    {modalData.pre ? (
                        <Box className={boxClasses.root}>
                            <ParseResponse data={modalData.pre}/>
                        </Box>
                    ) : (
                        <Alert severity="error">
                            Well, this in embarrassing&hellip; something went wrong.
                        </Alert>
                    )}
                </Paper>
            </Modal>
        </>
    );
}

export default ShortFormLead
